/**
 * WCAG relative luminance and contrast, plus the two derivations the app needs from a color it did
 * not choose.
 *
 * This lives in `shared/` because the same arithmetic has to run in two places that must never
 * disagree: `frontend/theme.ts` derives a readable label color for the accent the OWNER picked at
 * runtime, and `tests/theme.test.ts` asserts that derivation holds for accents nobody would pick.
 * A second copy of the formula in the test would be a test of itself.
 *
 * ⚠ Nothing here is imported by a published page. It is app-side only - a fan-facing page has no
 * theme to derive against (it renders one way, in the artist's own colors), and `shared/` is free
 * to hold app-only code as long as no `linkPage`/`bioPage`/`epk` renderer reaches for it.
 */

/** AA for body text. The bar every text-on-surface pairing in the app must clear. */
export const AA_TEXT = 4.5;
/** AA for a non-text graphical object - a chart mark, a border that carries meaning, an icon. */
export const AA_MARK = 3;

export interface Rgb {
	r: number;
	g: number;
	b: number;
}

/** `#rgb` / `#rrggbb` → channels. Returns null for anything else, so a caller can fall back. */
export function parseHex(hex: string): Rgb | null {
	const h = hex.trim().replace(/^#/, "");
	if (h.length === 3) {
		// charAt rather than spreading: this is hex, so code units ARE the characters, and the lint
		// rule guarding against decomposing rich text is right in general and noise here.
		const [r, g, b] = [0, 1, 2].map(i => parseInt(h.charAt(i) + h.charAt(i), 16));
		return Number.isNaN(r + g + b) ? null : { r, g, b };
	}
	if (h.length !== 6) return null;
	const r = parseInt(h.slice(0, 2), 16);
	const g = parseInt(h.slice(2, 4), 16);
	const b = parseInt(h.slice(4, 6), 16);
	return Number.isNaN(r + g + b) ? null : { r, g, b };
}

export function toHex({ r, g, b }: Rgb): string {
	const c = (v: number) =>
		Math.round(Math.max(0, Math.min(255, v)))
			.toString(16)
			.padStart(2, "0");
	return `#${c(r)}${c(g)}${c(b)}`;
}

/** WCAG 2.x relative luminance (sRGB, the same curve every contrast checker uses). */
export function luminance({ r, g, b }: Rgb): number {
	const lin = (v: number) => {
		const s = v / 255;
		return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
	};
	return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Contrast ratio, 1 (identical) … 21 (black on white). Order-independent, as the spec defines it. */
export function contrast(a: Rgb, b: Rgb): number {
	const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
	return (hi + 0.05) / (lo + 0.05);
}

/** Same, for two hex strings. Returns 1 (the worst possible) if either fails to parse. */
export function contrastHex(a: string, b: string): number {
	const x = parseHex(a);
	const y = parseHex(b);
	return x != null && y != null ? contrast(x, y) : 1;
}

/** Blend `from` toward `to` by `t` in 0…1. Linear in sRGB - good enough for a UI ramp. */
export function mix(from: Rgb, to: Rgb, t: number): Rgb {
	return { r: from.r + (to.r - from.r) * t, g: from.g + (to.g - from.g) * t, b: from.b + (to.b - from.b) * t };
}

export const WHITE: Rgb = { r: 255, g: 255, b: 255 };
/** Not #000: a pure-black label on a saturated fill reads as a hole. This is the app's ink. */
export const INK: Rgb = { r: 17, g: 19, b: 30 };
const BLACK: Rgb = { r: 0, g: 0, b: 0 };

/**
 * The end of the scale FARTHER from `color` - where to move something so it contrasts with `color`
 * more, not less.
 *
 * ⚠ Trivial and worth naming twice over. Writing it inline is how round 148 reproduced the bug it
 * was fixing: the first draft compared the two ends and took the NEARER one, so a white button label
 * went from 4.86:1 to 3.64:1 on hover - the old "+25 per channel" defect, one ternary later. And the
 * ends here are pure BLACK and WHITE, not the app's INK, which is the second half: **ink is a
 * preference about what a LABEL should look like, not a fact about which DIRECTION increases
 * contrast.** Using it as a direction cost the monotonicity this function exists to provide - with
 * a black accent, "away from white" landed on #11131e, which is LIGHTER than the accent, so the
 * hover moved back toward the label. Exposed by the gate, on the one accent nobody would pick.
 */
export function awayFrom(color: Rgb): Rgb {
	return contrast(color, BLACK) > contrast(color, WHITE) ? BLACK : WHITE;
}

/**
 * The label color for text sitting ON a solid fill of `fill` - white or the app's ink, whichever
 * reads better, falling back to pure black when neither clears `bar`.
 *
 * ⚠ The fallback is not defensive padding, it is the whole correctness argument, and the round that
 * wrote this function got it wrong first. **White-vs-pure-black is always safe**: the two cross at
 * the luminance where both give the same ratio, and that ratio is 4.58, so no fill in sRGB defeats
 * both. **White-vs-INK is not**, because INK is #11131e rather than #000 - that costs about half a
 * ratio point, and it opens a narrow band of mid-luminance fills (the light palette's `--viz-1` at
 * #2a78d6 is in it: 4.42 with white, 4.05 with ink) where the nicer-looking pair has no passing
 * member at all. So ink is a PREFERENCE, applied only where it wins, and legibility is the floor.
 *
 * `--on-warning` in `public/style.css` is this rule, hand-applied to one token before it was a rule.
 */
export function onColor(fill: Rgb, bar = AA_TEXT): Rgb {
	const nicer = contrast(WHITE, fill) >= contrast(INK, fill) ? WHITE : INK;
	if (contrast(nicer, fill) >= bar) return nicer;
	return contrast(WHITE, fill) >= contrast(BLACK, fill) ? WHITE : BLACK;
}

/**
 * Step `color` toward `toward` until it clears `bar` against every one of `surfaces` - the answer to
 * "the owner picked this accent; what may I write in it?".
 *
 * Walks in 2% steps and returns the FIRST value that clears, so a color that already passes comes
 * back untouched and one that doesn't moves the least distance that works. If even `toward` itself
 * fails (a surface too close to both ends to be escaped) the caller gets `toward` - the best
 * available, never a value that silently fails.
 */
export function stepUntilReadable(color: Rgb, toward: Rgb, surfaces: readonly Rgb[], bar = AA_TEXT): Rgb {
	for (let i = 0; i <= 50; i++) {
		const c = mix(color, toward, i / 50);
		if (surfaces.every(s => contrast(c, s) >= bar)) return c;
	}
	return toward;
}
